/**
 * Event index: maps every event route to its page component.
 * Single place to register event pages; App.js uses this for routes.
 *
 * Adding a new event with its own page:
 * 1. Add the event in the right platform config (cortexEvents, titanEvents, etc.) with path + slug.
 * 2. Create the page component in src/pages/<platform>/<Name>.js.
 * 3. Register here: add the component to the platform's slug→Component map below.
 * Routes are built automatically from the config path.
 */

import { getCortexEventsForListing } from './cortexEvents';
import { getTitanEventsForListing } from './titanEvents';
import { getJeltrEventsForListing } from './jeltrEvents';
import Conference2026 from '../pages/cortex/Conference2026';
import EssayCompetition2025 from '../pages/cortex/EssayCompetition2025';
import Workshop2026 from '../pages/cortex/Workshop2026';
import MemberRegistration from '../pages/cortex/MemberRegistration';
import KeynoteSpeech2026 from '../pages/cortex/KeynoteSpeech2026';
import MongolianCriticalZone from '../pages/titan/MongolianCriticalZone';
import CallForPapers2026 from '../pages/jeltr/CallForPapers2026';

const CORTEX_EVENT_PAGES = {
  'member-registration': MemberRegistration,
  'conference-2026': Conference2026,
  'essay-competition-2025': EssayCompetition2025,
  'workshop-2026': Workshop2026,
  'keynote-speech-2026': KeynoteSpeech2026,
};

const TITAN_EVENT_PAGES = {
  'mongolian-critical-zone': MongolianCriticalZone,
};

const JELTR_EVENT_PAGES = {
  'call-for-papers-2026': CallForPapers2026,
};

/**
 * Build route list from platform configs + page maps.
 * Returns [{ path, Component }, ...] for events that have a registered page.
 */
const buildEventRoutes = () => {
  const routes = [];
  getCortexEventsForListing().forEach((event) => {
    const Component = CORTEX_EVENT_PAGES[event.slug];
    if (Component) routes.push({ path: event.path, Component, platform: 'cortex', slug: event.slug });
  });
  getTitanEventsForListing().forEach((event) => {
    const Component = TITAN_EVENT_PAGES[event.slug];
    if (Component) routes.push({ path: event.path, Component, platform: 'titan', slug: event.slug });
  });
  getJeltrEventsForListing().forEach((event) => {
    const Component = JELTR_EVENT_PAGES[event.slug];
    if (Component) routes.push({ path: event.path, Component, platform: 'jeltr', slug: event.slug });
  });
  return routes;
};

/** All event routes (path + component). Use in App.js to render <Route path={...} element={<Component />} /> */
export const EVENT_ROUTES = buildEventRoutes();

/** Look up component by path (e.g. for dynamic title or breadcrumb) */
export const getEventComponentByPath = (path) => {
  const r = EVENT_ROUTES.find((x) => x.path === path);
  return r ? r.Component : null;
};
